"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useRef } from "react";
import type { ComponentPropsWithoutRef } from "react";

type PrefetchLinkProps = Omit<
  ComponentPropsWithoutRef<typeof Link>,
  "href" | "prefetch"
> & {
  href: string;
};

/**
 * A Link that skips Next's viewport prefetch and warms the route on intent
 * instead: pointer enter, touch contact, or keyboard focus.
 *
 * Next prefetches every Link the moment it scrolls into view, so an index of
 * thirty projects pulled thirty RSC payloads whether or not anyone opened one.
 * Measured on production, leaf project pages ran 4,376 prefetches against 13
 * real visits, and the blanket sweep was a quarter of the site's edge requests.
 *
 * In the App Router `prefetch={false}` disables viewport AND hover prefetch,
 * so the hover warm has to be re-added by hand. pointerenter covers both mouse
 * hover and touch contact, each of which leads the click by enough for the
 * payload to land, so navigation still feels instant on the rows people
 * actually reach for. Use it for indexes and walls of many low-intent links;
 * primary nav and next/back links keep the default prefetch.
 */
export function PrefetchLink({
  href,
  children,
  onPointerEnter,
  onFocus,
  ...rest
}: PrefetchLinkProps) {
  const router = useRouter();
  const warmed = useRef(false);

  const warm = () => {
    if (warmed.current) return;
    warmed.current = true;
    router.prefetch(href);
  };

  return (
    <Link
      {...rest}
      href={href}
      prefetch={false}
      onPointerEnter={(event) => {
        warm();
        onPointerEnter?.(event);
      }}
      onFocus={(event) => {
        warm();
        onFocus?.(event);
      }}
    >
      {children}
    </Link>
  );
}
